import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  SELECTED_OPTION_CHECKBOX,
  SELECTED_OPTION_RADIO,
  TAB_COMMON,
  TAB_POPULAR,
  VALUE_TYPE_MARKET,
} from '@/pages/home/components/NewRegionSelect/config';
import {
  ChildrenItemType,
  OptionsItemType,
  RegionSelectPropsType,
  RegionSelectValueType,
  TShortcutProps,
} from '@/pages/home/components/NewRegionSelect/type';
import {
  buildSelectedMarketsInfo,
  getRegionValue,
  injectGroupHeaders,
  isChildMatchSearch,
  isSelectableMarketItem,
  GROUP_HEADER_VALUE_PREFIX,
  resolveMarketsAfterCloseTag,
} from '@/pages/home/components/NewRegionSelect/util';
import {
  uniq,
  intersection,
  trim,
  isFunction,
  keyBy,
  isEmpty,
  difference,
  // isEqual,
  // sortBy,
} from 'lodash-es';

const useData= (props: RegionSelectPropsType) => {
  const {
    // 值
    value = null,
    // 单选项
    radioOptions = [],
    // 多选项
    options = [],
    // 热门选项
    popularList = [],
    // 快捷键选项
    shortcutOptions,
    defaultShortcutType,
    onShortcutClick,
    onCloseReginSelect,
    onTabChange,
    onChange,
    activeTab = TAB_COMMON,
    loading,
    defaultSelectedRegion,
    renderRightContent,
    searchTagMode = 'market',
  } = props;
  const [search, setSearch] = useState<string>(''); // 搜索框的值
  const [currentTab, setCurrentTab] = useState<string>(activeTab); // 搜索框的值
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [innerValue, setInnerValue] = useState<RegionSelectValueType | null>(
    value,
  ); // 内部value状态值，用来刷新组件，无其他作用
  const [selectedRegion, setSelectedRegion] = useState<string>(); // 内部value状态值，用来刷新组件，无其他作用

  useEffect(()=>{
    if(loading) return;
    const selectRegin = defaultSelectedRegion || options?.[0]?.value;
    setSelectedRegion(selectRegin || '');
  },[loading]);

  const valueRef = useRef<RegionSelectValueType | null>(value); // 内部value值
  const selectedType = useRef<string>(SELECTED_OPTION_RADIO); // 当前选择区域
  const searchInputRef = useRef<any>(null); // 当前选择区域

  // 同步外部值
  useMemo(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    setCurrentTab(activeTab);
  }, [activeTab]);

  // 取出所有的子项
  const { allChildren, allChildrenMap } = useMemo(() => {
    const allChildren: ChildrenItemType[] = [];
    const allChildrenMap: {[key: string]: ChildrenItemType;} = {};
    options.map((item: OptionsItemType) => {
      const { value, children = [] } = item;
      children?.forEach((subItem: OptionsItemType) => {
        if (subItem.isGroupHeader) {
          return;
        }
        const newItem = {
          ...subItem,
          parent: value,
          parentText: item?.text,
          parentTextCN: item?.textCN,
        };
        allChildren.push(newItem);
        allChildrenMap[subItem.value] = newItem;
      });
    });
    return {allChildren, allChildrenMap};
  }, [options]);

  const realOptionsAndChildren = useMemo(() => {
    const searchStr = trim(search).toLowerCase();
    if (!searchStr) {
      return {
        realOptions: options,
        realAllChildren: allChildren,
        realAllChildrenMap: keyBy(allChildren, (item) => item.value)
      };
    }

    const filterChildren: ChildrenItemType[] = [];
    const filterOptions = options?.map((item: OptionsItemType) => {
      const { text: parentText, textCN: parentTextCN, value, children = [] } = item;
      if(parentText?.toLowerCase().includes(searchStr) || parentTextCN?.toLowerCase().includes(searchStr)){
        children?.forEach((subItem: OptionsItemType) => {
          if (subItem.isGroupHeader) {
            return;
          }
          filterChildren.push({
            ...subItem,
            parent: value,
            parentText,
            parentTextCN,
          });
        });
        return item;
      }
      const filteredChildren = (item.children || []).filter((subItem: OptionsItemType) => {
        if (isChildMatchSearch(subItem, searchStr)) {
          filterChildren.push({
            ...subItem,
            parent: value,
            parentText,
            parentTextCN,
          });
          return true;
        }
        return false;
      });

      return {
        ...item,
        children: filteredChildren,
      };
    }) || [];

    const realOptions = filterOptions.filter((item) => item.children && item.children?.length > 0);

    return {
      realOptions,
      realAllChildren: filterChildren,
      realAllChildrenMap: keyBy(filterChildren, (item) => item.value)
    };
  }, [options, search]);

  // 当前鼠标左侧选中的 type；优先从搜索过滤后的 realOptions 取，保证 children 与右侧列表一致
  const { selectedRegionName, selectedRegionItem } = useMemo(() => {
    const regionItem =
      realOptionsAndChildren?.realOptions?.find(
        (item) => item.value === selectedRegion,
      ) ?? options?.find((item) => item.value === selectedRegion);
    // selectedRegion 在搜索过滤/选项刷新后可能暂时无匹配项，文案兜底空串避免 undefined 进入渲染与 locale 索引
    return {
      selectedRegionName: {
        'en-US': regionItem?.text ?? '',
        'zh-CN': regionItem?.textCN ?? '',
      },
      selectedRegionItem: regionItem,
    };
  }, [selectedRegion, realOptionsAndChildren, options]);

  // 搜索结果默认自动定位到第一个结果且展开右侧
  useEffect(() => {
    if (trim(search)) {
      setSelectedRegion(realOptionsAndChildren?.realOptions?.[0]?.value);
    }
  }, [search]);

  const setValue = (value: RegionSelectValueType) => {
    valueRef.current = value;
    setInnerValue(valueRef.current);
    onChange?.(value);
  };
  const radioValues: string[] = useMemo(() => radioOptions.map((item) => item.value), [radioOptions]);

  const { shortcutActive, handleClickShortcut } = useFilterAddShortcut({
    shortcutOptions,
    radioValues,
    defaultShortcutType,
    valueRef,
    options,
    allChildren,
    onShortcutClick,
    setValue,
  });

  const realRegionsList = useMemo(()=> {
    const { markets = [] } = valueRef.current || {};
    const { realOptions } = realOptionsAndChildren;
    const realRegionsList: string[] = [];
    const marketsSet = new Set(markets);
    realOptions?.forEach((item) => {
      const { children = [], value, disabled } = item || {};
      if (children?.length < 1) {
        return;
      }
      // const allSelected = children?.filter((item)=> !marketsSet.has(item.value)).length === 0;
      // handleClickRegionCheckBox方法处已调整，这边也需相应调整, 防止和children数据源长度不一致
      let allSelected = false;
      if (disabled) {
        allSelected = children?.filter((item)=> !marketsSet.has(item.value)).length === 0;
      } else {
        const availableChildren = children?.filter((item)=> !item?.disabled) || [];
        allSelected = availableChildren.length > 0 && availableChildren?.filter((item)=> !marketsSet.has(item.value)).length === 0;
      }

      if(allSelected){
        realRegionsList.push(value);
      }
    });
    return realRegionsList;
  },[realOptionsAndChildren, valueRef.current]);

  // 获取半选的父元素，并设置当前是radio还是checkbox
  const indeterminateRegions = useMemo(() => {
    const { markets = [] } = valueRef.current || {};
    const { realAllChildrenMap } = realOptionsAndChildren;
    const indeterminateRegions: string[] = [];
    // 选择是radio的情况
    if (
      radioOptions.length > 0 &&
      markets?.length === 1 &&
      radioValues.includes(markets?.[0])
    ) {
      selectedType.current = SELECTED_OPTION_RADIO;
      return [];
    }
    // 设置选择类型是checkbox
    selectedType.current = SELECTED_OPTION_CHECKBOX;
    markets?.forEach((val) => {
      const find = realAllChildrenMap?.[val];
      if (find && !realRegionsList?.includes(find.parent)) {
        indeterminateRegions.push(find.parent);
      }
    });
    return uniq(indeterminateRegions);
  }, [realOptionsAndChildren, radioOptions, radioValues, realRegionsList, valueRef.current]);


  // 获取右侧markets列表
  const realMarketsList: ChildrenItemType[] = useMemo(() => {
    let marketList: ChildrenItemType[] = [];
    const { realAllChildren } = realOptionsAndChildren;
    if (currentTab === TAB_POPULAR) {
      // popularList.forEach(val => {
      //   const find = allChildren.find(item => item.value === val);
      //   if (find) {
      //     marketList.push(find);
      //   }
      // });
      marketList = realAllChildren?.filter((item) =>
        popularList.includes(item.value),
      );
    } else {
      marketList = realAllChildren?.filter(
        (item) => item.parent === selectedRegion,
      );
    }
    return injectGroupHeaders(marketList);

  }, [realOptionsAndChildren, currentTab, popularList, selectedRegion]);

  const selectAllInfo = useMemo(() => {
    const { markets = [] } = valueRef.current || {};
    const displayMarketsValue = realMarketsList
      ?.filter(isSelectableMarketItem)
      ?.map((item) => item.value);
    // 使用loadsh工具判断是否已全部选中，文档https://www.lodashjs.com/docs/lodash.intersection
    // const marketsSelectAll = intersection(markets, displayMarketsValue).length === displayMarketsValue.length;
    // const regionSelectAll = regions.length === options.length;
    const regionSelectAll =
      realRegionsList?.length > 0 || indeterminateRegions?.length > 0;
    const marketsSelectAll =
      intersection(markets, displayMarketsValue)?.length > 0;
    return {
      marketsSelectAll,
      regionSelectAll,
    };
  }, [realMarketsList, realRegionsList, indeterminateRegions, valueRef]);

  const selectedMarketsInfo = useMemo(() => {
    const { markets = [], regions = [] } = valueRef.current || {};
    return buildSelectedMarketsInfo({
      markets,
      regions,
      searchTagMode,
      options,
      allChildrenMap,
      radioValues,
    });
  }, [valueRef.current, allChildrenMap, radioValues, searchTagMode, options]);

  const handleClickTab = (tab: string): void => {
    if (currentTab === tab) {
      return;
    }
    setSearch('');
    setCurrentTab(tab);
    onTabChange?.(tab);
    if (tab === TAB_POPULAR) {
      setValue({
        type: VALUE_TYPE_MARKET,
        regions: [],
        markets: [],
      });
    } else {
      const markets = radioOptions?.[0] ? [radioOptions[0].value] : [];
      setValue({
        type: VALUE_TYPE_MARKET,
        regions: [],
        markets,
      });
    }
  };

  // 点击radios，记录在markets里面，业务从markets获取到特殊值，做特殊处理
  const handleChangeRadio = (value: string): void => {
    // 设置区域是radio
    // selectedType.current = SELECTED_OPTION_RADIO;
    setValue({
      type: VALUE_TYPE_MARKET,
      regions: [],
      markets: [value],
    });
    // setSelectedRegion('');
    // onChange?.(valueRef?.current);
  };

  const handleClickRegion = (
    e: React.MouseEvent<HTMLDivElement>,
    region: OptionsItemType,
  ): void => {
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    setSelectedRegion(region.value);
  };

  // const handleClickGlobalDiv = (
  //   e: React.MouseEvent<HTMLDivElement>,
  //   region: OptionsItemType,
  // ): void => {
  //   e.preventDefault();
  //   e.stopPropagation();
  //   e.nativeEvent.stopImmediatePropagation();
  //   setSelectedRegion('');
  // };

  const handleClickRegionCheckBox = (region: OptionsItemType): void => {
    // debugger;
    const { value, children = [] } = region;
    setSelectedRegion(value);
    const { markets = [] } = valueRef.current || {};
    // 删除radios的值
    let newMarkets = [...markets].filter(
      (val: string) => !radioValues.includes(val),
    );

    // // 取出所有当前region下 markets的value
    // const childrenValues = children.map((item) => item.value);

    // 取出所有当前region下 markets的value，并过滤disabeld的market
    const childrenValues = children?.filter((item) => !item?.disabled)?.map((item) => item?.value);
    const childrenValuesSet = new Set(childrenValues);

    if (!realRegionsList.includes(value)) {
      newMarkets = uniq(newMarkets.concat(childrenValues));
    } else {
      newMarkets = newMarkets?.filter((val) => !childrenValuesSet.has(val));
    }
    setValue(getRegionValue(newMarkets, options, allChildren, radioValues));
    // onChange?.(valueRef?.current);
  };

  const handleChangeMarkets = (value: string[]) => {
    let realValue = value?.filter(
      (val: string) =>
        !radioValues.includes(val) &&
        !val.startsWith(GROUP_HEADER_VALUE_PREFIX),
    );
    realValue = uniq(realValue);
    // TAB_POPULAR 情况下直接设置markets
    let val: RegionSelectValueType = {
      type: VALUE_TYPE_MARKET,
      regions: [],
      markets: realValue,
    };

    // TAB_COMMON 情况下需计算包含的region
    if (currentTab === TAB_COMMON) {
      val = getRegionValue(realValue, options, allChildren, radioValues);
    }
    setValue(val);
  };

  const handleClickRegionSelectAll = () => {
    // const { realOptions } = realOptionsAndChildren;
    // let newMarkets: string[] = [];
    // realOptions.forEach((item) => {
    //   const { children = [] } = item;
    //   newMarkets = newMarkets.concat(children.map((item) => item.value));
    // });
    // setValue({
    //   type: VALUE_TYPE_REGION,
    //   regions: realOptions.map((item) => item.value),
    //   markets: newMarkets,
    // });

    // 过滤掉disabled的region
    const realOptions = realOptionsAndChildren?.realOptions?.filter((item) => !item?.disabled) || [];
    const { markets = [] } = valueRef.current || {};
    let newMarkets: string[] = [...markets].filter(
      (val: string) => !radioValues.includes(val),
    );
    realOptions?.forEach((item) => {
      // 过滤掉当前region下 disabled的market
      const children = item?.children?.filter((item) => !item?.disabled) || [];
      const childrenValues = children?.map((item) => item?.value);
      newMarkets = uniq(newMarkets.concat(childrenValues));
    });
    setValue(getRegionValue(newMarkets, options, allChildren, radioValues));
    // onChange?.(valueRef?.current);
  };

  // region clear all
  const handleClickRegionClearAll = (clearAll = false) => {
    // 直接设置成第一个radio的值，没有则置空
    let { markets = [] } = valueRef.current || {};
    const { realOptions } = realOptionsAndChildren;

    realOptions?.forEach((item) => {
      const { children = [] } = item;
      const childrenValues = children?.map((item) => item?.value);
      const childrenValuesSet = new Set(childrenValues);
      markets = markets?.filter(item => !childrenValuesSet.has(item));
    });
    const radioValue = radioValues?.[0] ? [radioValues?.[0]] : [];
    const finalMarkets = markets?.length > 0 && !clearAll ? markets : radioValue;
    setValue(getRegionValue(finalMarkets, options, allChildren, radioValues));
    // onChange?.(valueRef?.current);
  };

  // markets select all
  const handleClickMarketSelectAll = () => {
    const { markets = [] } = valueRef.current || {};
    // const addMarkets = realMarketsList.map((item) => item.value);
    // 过滤掉radioValues的选项
    const marketsCur = markets?.filter((item) => !radioValues?.includes(item));
    // 过滤掉disabeld的market
    const addMarkets = realMarketsList
      ?.filter((item) => isSelectableMarketItem(item) && !item?.disabled)
      ?.map((item) => item.value);
    const newMarkets = uniq(marketsCur?.concat(addMarkets));

    // 如果是TAB_POPULAR，直接设置type market
    if (currentTab === TAB_POPULAR) {
      setValue({
        type: VALUE_TYPE_MARKET,
        regions: [],
        markets: newMarkets,
      });
    } else {
      setValue(getRegionValue(newMarkets, options, allChildren, radioValues));
    }
    // onChange?.(valueRef?.current);
  };

  // markets clear all
  const handleClickMarketClearAll = () => {
    // 如果是TAB_POPULAR  直接清空
    if (currentTab === TAB_POPULAR) {
      setValue({
        type: VALUE_TYPE_MARKET,
        regions: [],
        markets: [],
      });
      return;
    }
    // 删除需要清掉的markets
    const { markets = [] } = valueRef.current || {};
    const delMarkets = realMarketsList
      ?.filter(isSelectableMarketItem)
      ?.map((item) => item.value);
    const delMarketsSet = new Set(delMarkets);
    const newMarkets = markets?.filter((val) => !delMarketsSet.has(val));
    setValue(getRegionValue(newMarkets, options, allChildren, radioValues));
    // onChange?.(valueRef?.current);
  };

  const handleCloseTag = (value: string) => {
    const { markets = [] } = valueRef.current || {};
    const newMarkets = resolveMarketsAfterCloseTag(
      markets,
      value,
      searchTagMode,
      options,
    );
    setValue(getRegionValue(newMarkets, options, allChildren, radioValues));
  };

  const handleClickSearchClose = () => {
    setSearch('');
    handleClickRegionClearAll(true);
  };

  const handleClickSearchWrap = () => {
    searchInputRef.current?.focus();
  };

  const handleCloseRegionSelect = () => {
    onCloseReginSelect(valueRef?.current);
  };

  // 设置焦点；使用自定义右侧内容（如 renderRightContent）且已选左侧项时不聚焦搜索框，避免抢占数值输入焦点
  useEffect(() => {
    if (selectedRegionItem && renderRightContent?.(selectedRegionItem, valueRef.current || { type: '', regions: [], markets: [] })) return;
    searchInputRef.current?.focus();
  });

  return {
    searchInputRef,
    valueRef,
    search,
    setSearch,
    currentTab,
    selectedRegion,
    selectedRegionName,
    selectedRegionItem,
    allChildren,
    realOptionsAndChildren,
    realMarketsList,
    indeterminateRegions,
    selectAllInfo,
    selectedType,
    selectedMarketsInfo,
    handleClickTab,
    handleChangeRadio,
    handleClickRegion,
    // handleClickGlobalDiv,
    handleClickRegionCheckBox,
    handleChangeMarkets,
    handleClickRegionSelectAll,
    handleClickRegionClearAll,
    handleClickMarketSelectAll,
    handleClickMarketClearAll,
    handleCloseTag,
    handleClickSearchClose,
    handleClickSearchWrap,
    handleCloseRegionSelect,
    shortcutActive,
    handleClickShortcut,
    realRegionsList
  };
};
export default useData;

/**
 * @example
 * const shortcutOptions=[
   {
    textCN: '全部',
    text: 'All',
    type: 'All',
    value: [],
    filter: (options,radioOptions)=>{
      const template=[...realOptions];
      const allChildren: ChildrenItemType[] = [];
      options.map((item: OptionsItemType) => {
        const { value, children = [] } = item;
        children.forEach((subItem: OptionsItemType) => {
          allChildren.push({
            ...subItem,
            parent: value,
          });
        });
      });
      return allChildren.map(task=>task.value);
    }
  },
  {
    textCN: '非中国',
    text: 'non-Chinese',
    type: 'non-Chinese',
    // value: ['chn'],
    filter: (options,radioOptions)=>{
      const template=[...options];
      return xxx
    }
  },
  {
    textCN: '中国',
    text: 'Chinese',
    type: 'Chinese',
    filter: (options,radioOptions)=>{
      const template=[...options];
      return xxx
    }
  }
];
 * @returns 针对快捷栏定制化
 * 组件设计文档
 * https://doc.weixin.qq.com/mind/m4_AeUAJAaiABgNrI89QWFR0C1e7lhuC?scode=AJEAIQdfAAoQ3GJcooAeUAJAaiABg
 */


export const useFilterAddShortcut = (props: TShortcutProps) => {
  const {
    shortcutOptions,
    radioValues = [],
    options = [],
    allChildren,
    // defaultShortcutType,
    setValue,
    onShortcutClick,
    valueRef,
  } = props;
  // 最新值
  const { markets = [] } = valueRef?.current || {};
  // 每个快捷键对应的value
  const shortcutList = useMemo(() => {
    // 配置了快捷键功能才进行计算
    // 依次求出每个快捷键的value, 在根据用户点选的下拉进行比较，判断是否高亮、取消高亮
    if (!isEmpty(shortcutOptions) && !isEmpty(options)) {
      const shortcutList = shortcutOptions?.map(
        (item: {[key : string]: unknown;}) => {
          // 查找用户自定义的过滤条件，需吐出过滤后的数组,
          const { filter: optionFilter, value: optionValue, type, canClear = true } = item || {};
          const filterData = optionValue ? optionValue : (isFunction(optionFilter) && optionFilter?.(options, radioValues)) || [];
          return {
            type,
            canClear,
            value: getRegionValue(
              filterData,
              options,
              allChildren,
              radioValues,
            )
          };
        },
      );
      return shortcutList;
    }
  }, [allChildren, options, radioValues, shortcutOptions]);

  const shortcutActive = useMemo(() => {
    // 求出高亮值
    if (!isEmpty(shortcutList)) {
      const shortcutActive = shortcutList?.find(
        (task: { value: { markets: Array<string>; }; }) => {
          const item = task?.value?.markets;
          // 长度相等的情况下，再次比较
          if (item.length === markets?.length) {
            return difference(markets, item).length === 0;
          }
          return false;
        },
      )?.type;
      return shortcutActive;
    }
    return undefined;
  }, [markets, shortcutList]);

  // 点击高亮
  const handleClickShortcut = (item: string) => {
    const { value, canClear = true } = shortcutList?.find((task: { type: string; }) => task?.type === item) || {};
    // 如果当前点击的快捷键和当前高亮值一致
    if(shortcutActive && shortcutActive === item && canClear) {
      setValue?.({
        type: VALUE_TYPE_MARKET,
        regions: [],
        markets: [],
      });
      onShortcutClick?.(item);
      return;
    }
    // 正常勾选快捷选项
    value && setValue?.(value);
    onShortcutClick?.(item);
  };

  return {
    shortcutActive,
    handleClickShortcut,
  };
};
